import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import { formatBaseQty } from '#/modules/imsMgmt/inventoryStock/helper/inventoryStockFilterOptions';
import { searchWarehouseFilterOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { searchWarehouseLocationFilterOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseLocationSelectOptions';

export function buildInvStockPickerColumns({
  filters = {},
  onFilterChange,
  allowWarehouseFilter = true,
  excludeWarehouseId = null,
  warehouseLabelCache,
  locationLabelCache,
} = {}) {
  async function searchWarehouseOptions(args) {
    const options = await searchWarehouseFilterOptions(args);
    if (!excludeWarehouseId) {
      return options;
    }
    const excludeId = String(excludeWarehouseId);
    return options.filter((option) => String(option.value) !== excludeId);
  }

  const warehouseColumn = {
    id: 'warehouseId',
    header: '來源倉',
    accessorFn: (row) => row.warehouse?.name || '-',
    size: 150,
    minSize: 140,
    enableColumnFilter: allowWarehouseFilter,
    meta: {
      cellClassName: 'td_left',
      ...(allowWarehouseFilter
        ? {
          ...createAsyncMultiFilterMeta({
            key: 'warehouseId',
            label: '來源倉',
            searchOptions: searchWarehouseOptions,
            filterValue: filters.warehouseId,
            onChange: (value) => onFilterChange?.('warehouseId', value),
            resolveFilterDisplay: (values) => (
              resolveFromLabelCache(warehouseLabelCache, values)
            ),
            rememberSelectedOptions: (options, values) => {
              rememberAsyncFilterLabels(warehouseLabelCache, options, values);
            },
          }),
        }
        : {}),
    },
  };

  const locationColumn = {
    id: 'locationId',
    header: '儲位',
    accessorFn: (row) => row.warehouse?.location?.name || '-',
    size: 150,
    minSize: 140,
    enableColumnFilter: true,
    meta: {
      cellClassName: 'td_left',
      dependsOnFilters: { warehouseId: filters.warehouseId },
      ...createAsyncMultiFilterMeta({
        key: 'locationId',
        label: '儲位',
        searchOptions: searchWarehouseLocationFilterOptions,
        filterValue: filters.locationId,
        onChange: (value) => onFilterChange?.('locationId', value),
        resolveFilterDisplay: (values) => (
          resolveFromLabelCache(locationLabelCache, values)
        ),
        rememberSelectedOptions: (options, values) => {
          rememberAsyncFilterLabels(locationLabelCache, options, values);
        },
      }),
    },
  };

  return [
    {
      id: 'stockNo',
      header: '庫存編號',
      accessorKey: 'stockNo',
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'stockNo',
        label: '庫存編號',
        filterValue: filters.stockNo,
        onChange: (v) => onFilterChange?.('stockNo', v),
        placeholder: '搜尋庫存編號',
      }),
    },
    {
      id: 'mtrNo',
      header: '料號',
      accessorFn: (row) => row.mtr?.mtrNo ?? '-',
      size: 140,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'mtrNo',
          label: '料號',
          filterValue: filters.mtrNo,
          onChange: (v) => onFilterChange?.('mtrNo', v),
          placeholder: '搜尋料號',
        }),
      },
    },
    {
      id: 'mtrName',
      header: '名稱',
      accessorFn: (row) => row.mtr?.name ?? '-',
      size: 180,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'mtrName',
          label: '名稱',
          filterValue: filters.mtrName,
          onChange: (v) => onFilterChange?.('mtrName', v),
          placeholder: '搜尋名稱',
        }),
      },
    },
    warehouseColumn,
    locationColumn,
    {
      id: 'baseQty',
      header: '可用量',
      accessorFn: (row) => formatBaseQty(row),
      size: 120,
      enableSorting: false,
    },
  ];
}
